import { useCallback } from 'react';
import { toast } from '@/components/hooks/use-toast';
import { guidedPlanningApi } from '@/features/guided-financial-planning/api';
import { useSession } from '@/features/guided-financial-planning/context';
import { isSamePolicy } from '@/features/guided-financial-planning/utils/finalized-policies';

const KEPT_POLICY_NOTICE = {
  title: 'Earlier policy kept',
  description:
    'The policy from the earlier finalized plan is still on the client: an application has been started on it, or it couldn’t be removed. Remove it from the Funding table if it no longer applies.',
};

/**
 * For a reopened session (see `useReopenSession`): removes the policies the earlier finalization
 * saved that the plan no longer includes. `keep` is what the plan saves now; a policy finalizing
 * saves again under the same id is updated in place, not removed.
 *
 * Removing a policy funding source also deletes the applications started on it, so a policy with an
 * application stays, and the agent is told. Resolves to the policies removed.
 */
export function useReplaceFinalizedPolicies() {
  const { session } = useSession();
  const previouslyFinalizedPolicies = session?.stepData?.previouslyFinalizedPolicies;

  return useCallback(
    async (keep: readonly FinalizedPolicyRef[]): Promise<FinalizedPolicyRef[]> => {
      const replaced = (previouslyFinalizedPolicies ?? []).filter(
        ref => !keep.some(kept => isSamePolicy(kept, ref)),
      );
      if (replaced.length === 0) return [];

      let policiesWithApplications: Set<string>;
      try {
        const clientIds = [...new Set(replaced.map(ref => ref.clientId))];
        const applications = await Promise.all(
          clientIds.map(clientId => guidedPlanningApi.getPolicyApplications(clientId)),
        );
        policiesWithApplications = new Set(applications.flat().map(a => a.fundingSourceId));
      } catch {
        toast(KEPT_POLICY_NOTICE);
        return [];
      }

      const removed: FinalizedPolicyRef[] = [];
      for (const ref of replaced) {
        if (policiesWithApplications.has(ref.fundingSourceId)) continue;
        try {
          await guidedPlanningApi.deletePolicyFundingSource(ref);
          removed.push(ref);
        } catch {
          // Left on the client; the notice below covers it.
        }
      }

      if (removed.length < replaced.length) toast(KEPT_POLICY_NOTICE);
      return removed;
    },
    [previouslyFinalizedPolicies],
  );
}
